import { ErrorModal, MesfModal } from "@dexteel/mesf-core";
import { Checkbox, FormControlLabel, Grid, TextField } from "@mui/material";
import { get } from "lodash-es";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { FooterModalButtons } from "../../../../../controls/buttons/FooterModalButtons";
import { ConfirmModal } from "../../../../../controls/modals/ConfirmModal";
import { NotificationSnackBar } from "../../../../../controls/snackbars/notification-snackbar";
import {
  CATALOG_TUNDISH_INITIAL_VALUES,
  CatalogTundish,
} from "../../../models/CatalogTundish";
import {
  deleteCatalogTundish,
  upsertCatalogTundish,
} from "../../../repositories/CatalogTundishRepository";
import { validateNoSpecialChars } from "../../validates";
import { useCatalogTundishContext } from "../context/CatalogTundishContext";

type onModalHideProps = {
  entityId?: number;
  shouldUpdate?: boolean;
};

type Props = {
  tundish: CatalogTundish | null;
  showModal: "new" | "edit" | "delete" | "";
  onHide: ({ entityId, shouldUpdate }: onModalHideProps) => void;
  refreshData: Function;
};

export const TundishModal = ({
  tundish,
  showModal,
  onHide,
  refreshData,
}: Props) => {
  const { state } = useCatalogTundishContext();

  const [notificationMessage, setNotificationMessage] = useState("");
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [error, setError] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [existingTundish, setExistingTundish] = useState("");
  const [title, setTitle] = useState("");

  const {
    control,
    getValues,
    handleSubmit,
    reset,
    formState: { dirtyFields },
  } = useForm<CatalogTundish>({
    defaultValues: CATALOG_TUNDISH_INITIAL_VALUES,
  });

  const isFormDirty =
    !(Object.keys(dirtyFields).length === 0) || showModal == "new";

  const onSubmit: SubmitHandler<CatalogTundish> = async (
    data: CatalogTundish,
  ) => {
    setIsSubmitLoading(true);
    const tundishs: CatalogTundish[] = state.tundishes || [];
    if (showModal === "delete") {
      setIsSubmitLoading(true);
      const resp = await deleteCatalogTundish(tundish?.TundishId ?? null);
      if (resp.ok) {
        await refreshData();
        onHide({ shouldUpdate: true });
        setNotificationMessage("The tundish was deleted successfully");
      } else {
        setError(resp.message);
      }
      setIsSubmitLoading(false);
    } else {
      if (
        tundishs.some(
          (tundish) =>
            tundish.TundishName === data.TundishName &&
            tundish.TundishId !== data.TundishId,
        )
      ) {
        setExistingTundish(data.TundishName);
        setConfirmOpen(true);
      } else {
        await performUpsert(data);
      }
    }
  };

  const performUpsert = async (data: CatalogTundish) => {
    const resp = await upsertCatalogTundish(data);
    if (resp.ok) {
      const entityId = get(resp, "data.returnValue", 0);
      await refreshData();
      setNotificationMessage("The tundish was saved successfully");
      onHide({ entityId: entityId, shouldUpdate: true });
    } else {
      setError(resp.message);
      onHide({ shouldUpdate: false });
    }
    setIsSubmitLoading(false);
  };

  useEffect(() => {
    if (showModal != "") {
      setTitle(showModal.toUpperCase());
      reset(tundish || CATALOG_TUNDISH_INITIAL_VALUES);
    }
  }, [showModal]);

  return (
    <>
      <ConfirmModal
        show={confirmOpen}
        onHide={(proceed: boolean) => {
          if (proceed) {
            performUpsert(getValues());
            onHide({ shouldUpdate: true });
          }
          setConfirmOpen(false);
          setIsSubmitLoading(false);
        }}
        existingName={existingTundish}
        label="Tundish"
      />
      <MesfModal
        title={`${title} TUNDISH`}
        open={showModal != ""}
        handleClose={() => onHide({ shouldUpdate: false })}
        id="tundish-modal"
        maxWidth="sm"
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <MesfModal.Content dividers style={{ padding: "15px 30px" }}>
            <Grid container spacing={1}>
              <Grid item xs={12} md={12}>
                <Controller
                  name="TundishName"
                  control={control}
                  rules={{
                    required: "Name is required",
                    validate: validateNoSpecialChars,
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      value={field.value}
                      onChange={field.onChange}
                      label="Name"
                      variant="outlined"
                      fullWidth
                      margin="dense"
                      autoComplete="off"
                      error={!!error}
                      helperText={error?.message}
                      disabled={showModal === "delete"}
                    />
                  )}
                />
              </Grid>
              <Grid item sm={12} xs={12}>
                <Controller
                  name="Enabled"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={field.value}
                          color="primary"
                          onChange={field.onChange}
                        />
                      }
                      label="Enabled"
                      disabled={showModal === "delete"}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </MesfModal.Content>
          <MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
            <FooterModalButtons
              isSubmitLoading={isSubmitLoading}
              onHide={() => onHide({ shouldUpdate: false })}
              buttonLabel={showModal === "delete" ? "Delete" : "Save"}
              buttonColor={showModal === "delete" ? "secondary" : "primary"}
              disabled={!isFormDirty && showModal !== "delete"}
            />
          </MesfModal.Actions>
        </form>
      </MesfModal>
      <NotificationSnackBar
        message={notificationMessage}
        onHide={() => setNotificationMessage("")}
      />
      <ErrorModal
        error={error}
        onHide={() => setError("")}
        title="Error with Tundish"
      />
    </>
  );
};
